/**
 * Form-state hook for `BankTransactionClassificationPanel`. Tracks the
 * top-level type selection plus per-type state (Contribution fields,
 * Expenditure fields, Exclude-from-report note). Per-type state clears on
 * type switch so a draft Contribution doesn't leak into an Expenditure /
 * Exclude path.
 *
 * ADR-014 / EFR-002: the retired `'transfer'` + `'dismiss'` paths collapsed
 * into the single `'exclude'` path (Exclude from report) — one optional
 * free-text note replaces both the transfer category and the dismiss reason.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReceiptNature,
  ElectionDesignation,
  ExpenditureCategory,
  ExpenditureClassificationSubCategory,
  InKindNature,
  PaymentMethod,
  assertNever,
  FederalTransferQualification,
} from '@signalvote/shared';
import type { BankTransactionClassificationHintDto } from '@signalvote/shared';

export type BankTransactionClassificationType =
  | 'contribution'
  | 'expenditure'
  // ADR-014 — the single non-reporting off-ramp. Carries one optional
  // free-text note; the footer "Exclude from report" fires the
  // exclude-from-report endpoint. Collapses the retired 'transfer'/'dismiss'.
  // Check deposits (retired 2026-07-14) also resolve here.
  | 'exclude';

export type BankTransactionSubmissionState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

/**
 * BSW-008: prefill seed sourced from BSW-005's in-memory suggestion
 * derivation. All fields are optional — when none are present, the
 * panel starts with empty form state.
 */
export interface BankTransactionContributionPrefill {
  contactId?: string | null;
  paymentMethod?: PaymentMethod | null;
  referenceNumber?: string | null;
}

interface UseFormArgs {
  /**
   * BSW-008: starting suggestions from BSW-005's suggestion derivation —
   * apply once on row mount and re-apply only when the suggestion seed
   * changes (the suggestion mutation is driven from outside the panel by
   * BSW-011's workspace refetch).
   */
  contributionPrefill?: BankTransactionContributionPrefill;
  /**
   * USI-008: skip the "pick a type" step by seeding the panel into a
   * specific path on mount. The triage modal's bank-line picker decides
   * the path via tile click; the panel then renders the corresponding
   * body immediately. Treasurer can still re-pick via the TypeToggle.
   */
  initialSelectedType?: BankTransactionClassificationType | null;
  /**
   * The bank line's classification hint, when it has one. Seeds the Expenditure path on mount —
   * general expenditure, payee, category and (when the hint has one) purpose — all
   * treasurer-editable.
   */
  expenditureHint?: BankTransactionClassificationHintDto | null;
  /**
   * AFU-002: when true (AL campaign), classifying a bank line as an in-kind
   * contribution requires a chosen in-kind nature before Classify unlocks
   * (the AL builder throws MissingInKindNature without it). Non-AL keeps the
   * nature optional.
   */
  requireInKindNature?: boolean;
}

export interface UseBankTransactionClassificationFormResult {
  selectedType: BankTransactionClassificationType | null;
  setSelectedType: (type: BankTransactionClassificationType | null) => void;

  // Per-type sub-category — populated by BSW-009 (Expenditure dropdown).
  // Kept as opaque string here so BSW-007 doesn't lock a specific shape;
  // the Expenditure path narrows it via `expenditureSubCategory` below.
  subCategory: string | null;
  setSubCategory: (value: string | null) => void;

  // ===== Expenditure-path state (BSW-009) =====
  expenditureSubCategory: ExpenditureClassificationSubCategory | null;
  setExpenditureSubCategory: (value: ExpenditureClassificationSubCategory | null) => void;
  expenditureContactId: string;
  setExpenditureContactId: (id: string) => void;
  expenditureCategory: ExpenditureCategory;
  setExpenditureCategory: (c: ExpenditureCategory) => void;
  expenditurePurpose: string;
  /** The classification hint the Expenditure path was seeded from, or null. */
  expenditureHint: BankTransactionClassificationHintDto | null;
  /** Which Expenditure fields still hold the hint's value — each is labelled "from hint" until
   *  the treasurer edits it. All false without a hint. */
  expenditureFieldsFromHint: ExpenditureFieldsFromHint;
  setExpenditurePurpose: (s: string) => void;
  // ADR-031 — the debt this classified expenditure settles (null = not a debt payment).
  expenditureSettlesDebtId: string | null;
  setExpenditureSettlesDebtId: (v: string | null) => void;
  // The loan this classified expenditure repays (null = not a loan repayment). In Louisiana this
  // is what lets a repayment file at all — it reports on Schedule B beneath the loan it repays.
  expenditureRepaysLoanId: string | null;
  setExpenditureRepaysLoanId: (v: string | null) => void;
  expenditureRefundOfContributionId: string;
  setExpenditureRefundOfContributionId: (id: string) => void;
  expenditureShowQuickCreate: boolean;
  openExpenditureQuickCreate: () => void;
  cancelExpenditureQuickCreate: () => void;

  // ===== Contribution-path state (BSW-008) =====
  contactId: string;
  setContactId: (id: string) => void;
  contributionType: ReceiptNature;
  setContributionType: (t: ReceiptNature) => void;
  electionDesignation: ElectionDesignation;
  setElectionDesignation: (d: ElectionDesignation) => void;
  // #144 — the off-cycle year (null = own cycle). Set together with the designation by the shared
  // election picker; carried into the classify request so a bank-line off-cycle gift isn't dropped.
  electionYear: number | null;
  setElectionYear: (y: number | null) => void;
  paymentMethod: PaymentMethod | null;
  setPaymentMethod: (p: PaymentMethod | null) => void;
  referenceNumber: string;
  setReferenceNumber: (s: string) => void;
  inKindFairMarketValueBasis: string;
  setInKindFairMarketValueBasis: (s: string) => void;
  inKindNature: InKindNature | null;
  setInKindNature: (v: InKindNature | null) => void;
  /**
   * #711 — WHICH relationship makes a federal transfer exempt from the contribution limits
   * (11 CFR § 110.3(c)). `None` is a real answer: the receipt meters like any other committee gift.
   */
  transferQualification: FederalTransferQualification;
  setTransferQualification: (v: FederalTransferQualification) => void;
  notes: string;
  setNotes: (s: string) => void;
  showQuickCreate: boolean;
  openQuickCreate: () => void;
  cancelQuickCreate: () => void;

  // ===== Exclude-from-report-path state (ADR-014 / EFR-002) =====
  // One optional free-text note. Empty/whitespace is sent as null and omitted
  // from the audit JSON server-side; content is stored verbatim.
  excludeNote: string;
  setExcludeNote: (s: string) => void;

  // Gate-override rule codes — used by `GateStack` in BSW-008/009.
  overriddenRuleCodes: ReadonlySet<string>;
  addOverride: (ruleCode: string) => void;
  clearOverrides: () => void;

  submission: BankTransactionSubmissionState;
  setSubmission: (state: BankTransactionSubmissionState) => void;

  /**
   * BSW-007 ship-shape: shell-time `canClassify` is `selectedType !== null`.
   * BSW-008 tightens for the Contribution path: a contact must be picked
   * and (when InKind) the FMV basis must be non-empty. Exclude path stays
   * "type !== null" (the note is optional).
   */
  canClassify: boolean;

  reset: () => void;
}

export interface ExpenditureFieldsFromHint {
  kind: boolean;
  payee: boolean;
  category: boolean;
  purpose: boolean;
}

export function useBankTransactionClassificationForm(
  args: UseFormArgs = {},
): UseBankTransactionClassificationFormResult {
  const { contributionPrefill, initialSelectedType, requireInKindNature } = args;
  const expenditureHint = args.expenditureHint ?? null;
  const [selectedType, setSelectedTypeRaw] =
    useState<BankTransactionClassificationType | null>(initialSelectedType ?? null);
  const [subCategory, setSubCategory] = useState<string | null>(null);

  // Contribution-path state. Always tracked (initialized to defaults) so
  // each path-specific component can read without null checks. Per-type
  // state clears on type switch via setSelectedType below.
  const [contactId, setContactId] = useState<string>(
    contributionPrefill?.contactId ?? '',
  );
  const [contributionType, setContributionType] = useState<ReceiptNature>(
    ReceiptNature.Cash,
  );
  const [electionDesignation, setElectionDesignation] = useState<ElectionDesignation>(
    ElectionDesignation.Undesignated,
  );
  const [electionYear, setElectionYear] = useState<number | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    contributionPrefill?.paymentMethod ?? null,
  );
  const [referenceNumber, setReferenceNumber] = useState<string>(
    contributionPrefill?.referenceNumber ?? '',
  );
  const [inKindFairMarketValueBasis, setInKindFairMarketValueBasis] = useState('');
  const [inKindNature, setInKindNature] = useState<InKindNature | null>(null);
  // #711 — starts at None: an unanswered transfer meters like any other committee gift. Silence
  // must never buy the limit exemption.
  const [transferQualification, setTransferQualification] =
    useState<FederalTransferQualification>(FederalTransferQualification.None);
  const [notes, setNotes] = useState('');
  // ADR-014 / EFR-002 — optional free-text note for the Exclude path.
  const [excludeNote, setExcludeNote] = useState('');
  const [showQuickCreate, setShowQuickCreate] = useState(false);

  // Expenditure-path state. Always tracked (initialized to defaults) so each
  // sub-category renderer can read without null checks. Cleared when the
  // user switches away from the Expenditure type via setSelectedType.
  // A classification hint seeds the general-expenditure fields from the campaign's own past
  // classification of this payee.
  const [expenditureSubCategory, setExpenditureSubCategoryRaw] =
    useState<ExpenditureClassificationSubCategory | null>(
      expenditureHint ? ExpenditureClassificationSubCategory.GeneralExpenditure : null,
    );
  const [expenditureContactId, setExpenditureContactId] = useState<string>(
    expenditureHint?.payeeContactId ?? '',
  );
  const [expenditureCategory, setExpenditureCategory] = useState<ExpenditureCategory>(
    expenditureHint?.category ?? ExpenditureCategory.Operating,
  );
  const [expenditurePurpose, setExpenditurePurpose] = useState<string>(
    expenditureHint?.purpose ?? '',
  );
  const [expenditureSettlesDebtId, setExpenditureSettlesDebtId] = useState<string | null>(null);
  const [expenditureRepaysLoanId, setExpenditureRepaysLoanId] = useState<string | null>(null);
  const [expenditureRefundOfContributionId, setExpenditureRefundOfContributionId] =
    useState<string>('');
  const [expenditureShowQuickCreate, setExpenditureShowQuickCreate] = useState(false);

  const [overriddenRuleCodes, setOverriddenRuleCodes] = useState<Set<string>>(
    () => new Set(),
  );
  const [submission, setSubmission] = useState<BankTransactionSubmissionState>(
    { kind: 'idle' },
  );

  // Re-apply suggestions when the prefill seed identity changes (e.g.
  // the workspace switches the panel from BT-A to BT-B with a different
  // suggested contact / payment method). We only seed fields the
  // treasurer hasn't touched — once they pick a contact, swapping the
  // suggestion shouldn't yank their selection out from under them.
  const prefillKey = `${contributionPrefill?.contactId ?? ''}|`
    + `${contributionPrefill?.paymentMethod ?? ''}|`
    + `${contributionPrefill?.referenceNumber ?? ''}`;
  useEffect(() => {
    if (contributionPrefill?.contactId && !contactId) {
      setContactId(contributionPrefill.contactId);
    }
    if (contributionPrefill?.paymentMethod && paymentMethod === null) {
      setPaymentMethod(contributionPrefill.paymentMethod);
    }
    if (contributionPrefill?.referenceNumber && referenceNumber === '') {
      setReferenceNumber(contributionPrefill.referenceNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  // Switching type clears the per-type state that no longer applies. Keeps
  // the shell from leaking stale Contribution state into the Expenditure
  // path (BSW-009 relies on this invariant).
  const setSelectedType = useCallback(
    (next: BankTransactionClassificationType | null) => {
      setSelectedTypeRaw(next);
      setSubCategory(null);
      setOverriddenRuleCodes(new Set());
      setSubmission({ kind: 'idle' });
      if (next !== 'exclude') {
        setExcludeNote('');
      }
      if (next !== 'contribution') {
        setContactId('');
        setContributionType(ReceiptNature.Cash);
        setElectionDesignation(ElectionDesignation.Undesignated);
        setElectionYear(null);
        setPaymentMethod(null);
        setReferenceNumber('');
        setInKindFairMarketValueBasis('');
        setInKindNature(null);
        setNotes('');
        setShowQuickCreate(false);
      }
      if (next !== 'expenditure') {
        setExpenditureSubCategoryRaw(null);
        setExpenditureContactId('');
        setExpenditureCategory(ExpenditureCategory.Operating);
        setExpenditurePurpose('');
        setExpenditureSettlesDebtId(null);
        setExpenditureRefundOfContributionId('');
        setExpenditureShowQuickCreate(false);
      }
    },
    [],
  );

  // Switching the Expenditure sub-category clears any per-sub-category state
  // that no longer applies. Keeps a treasurer who started a general
  // expenditure but switched to BankFee from sending a stale contactId.
  const setExpenditureSubCategory = useCallback(
    (next: ExpenditureClassificationSubCategory | null) => {
      setExpenditureSubCategoryRaw(next);
      setExpenditureContactId('');
      setExpenditureCategory(ExpenditureCategory.Operating);
      setExpenditurePurpose('');
      setExpenditureSettlesDebtId(null);
      setExpenditureRefundOfContributionId('');
      setExpenditureShowQuickCreate(false);
    },
    [],
  );

  const openExpenditureQuickCreate = useCallback(() => {
    setExpenditureShowQuickCreate(true);
  }, []);
  const cancelExpenditureQuickCreate = useCallback(() => {
    setExpenditureShowQuickCreate(false);
  }, []);
  const addOverride = useCallback((ruleCode: string) => {
    setOverriddenRuleCodes((prev) => {
      const next = new Set(prev);
      next.add(ruleCode);
      return next;
    });
  }, []);

  const clearOverrides = useCallback(() => {
    setOverriddenRuleCodes(new Set());
  }, []);

  const openQuickCreate = useCallback(() => {
    setShowQuickCreate(true);
  }, []);

  const cancelQuickCreate = useCallback(() => {
    setShowQuickCreate(false);
  }, []);

  const reset = useCallback(() => {
    setSelectedTypeRaw(null);
    setSubCategory(null);
    setContactId('');
    setContributionType(ReceiptNature.Cash);
    setElectionDesignation(ElectionDesignation.Undesignated);
    setElectionYear(null);
    setPaymentMethod(null);
    setReferenceNumber('');
    setInKindFairMarketValueBasis('');
    setInKindNature(null);
    setNotes('');
    setExcludeNote('');
    setShowQuickCreate(false);
    setExpenditureSubCategoryRaw(null);
    setExpenditureContactId('');
    setExpenditureCategory(ExpenditureCategory.Operating);
    setExpenditurePurpose('');
    setExpenditureSettlesDebtId(null);
    setExpenditureRefundOfContributionId('');
    setExpenditureShowQuickCreate(false);
    setOverriddenRuleCodes(new Set());
    setSubmission({ kind: 'idle' });
  }, []);

  const canClassify = useMemo(() => {
    if (selectedType === null) return false;
    if (selectedType === 'contribution') {
      if (!contactId) return false;
      if (
        contributionType === ReceiptNature.InKind
        && inKindFairMarketValueBasis.trim() === ''
      ) {
        return false;
      }
      // AFU-002: AL in-kind needs a nature before it can be classified+filed.
      if (
        requireInKindNature
        && contributionType === ReceiptNature.InKind
        && inKindNature === null
      ) {
        return false;
      }
      return true;
    }
    if (selectedType === 'expenditure') {
      // BSW-009: each sub-category has its own required-fields contract.
      if (expenditureSubCategory === null) return false;
      switch (expenditureSubCategory) {
        case ExpenditureClassificationSubCategory.GeneralExpenditure:
          // The catch-all requires a real payee + a purpose.
          return expenditureContactId !== ''
            && expenditurePurpose.trim() !== '';
        case ExpenditureClassificationSubCategory.Refund:
          return expenditureRefundOfContributionId !== '';
        case ExpenditureClassificationSubCategory.ProcessingFee:
        case ExpenditureClassificationSubCategory.BankFee:
          // Server resolves the payee — sub-category pick alone unlocks Classify.
          return true;
        default:
          return assertNever(expenditureSubCategory);
      }
    }
    // The exclude path keeps the shell-time "type was picked" gate — the
    // exclude note is optional, so the footer is always enabled.
    return true;
  }, [
    selectedType,
    contactId,
    contributionType,
    inKindFairMarketValueBasis,
    inKindNature,
    requireInKindNature,
    expenditureSubCategory,
    expenditureContactId,
    expenditurePurpose,
    expenditureRefundOfContributionId,
  ]);

  // A field is "from hint" while it still holds the hint's value; editing it clears the label.
  const expenditureFieldsFromHint = useMemo<ExpenditureFieldsFromHint>(() => {
    const isGeneral =
      expenditureHint !== null
      && expenditureSubCategory === ExpenditureClassificationSubCategory.GeneralExpenditure;
    return {
      kind: isGeneral,
      payee: isGeneral && expenditureContactId === expenditureHint.payeeContactId,
      category: isGeneral && expenditureCategory === expenditureHint.category,
      purpose:
        isGeneral
        && expenditureHint.purpose !== null
        && expenditurePurpose === expenditureHint.purpose,
    };
  }, [
    expenditureHint,
    expenditureSubCategory,
    expenditureContactId,
    expenditureCategory,
    expenditurePurpose,
  ]);

  return {
    selectedType,
    setSelectedType,
    subCategory,
    setSubCategory,

    contactId,
    setContactId,
    contributionType,
    setContributionType,
    electionDesignation,
    setElectionDesignation,
    electionYear,
    setElectionYear,
    paymentMethod,
    setPaymentMethod,
    referenceNumber,
    setReferenceNumber,
    inKindFairMarketValueBasis,
    setInKindFairMarketValueBasis,
    inKindNature,
    setInKindNature,
    transferQualification,
    setTransferQualification,
    notes,
    setNotes,
    excludeNote,
    setExcludeNote,
    showQuickCreate,
    openQuickCreate,
    cancelQuickCreate,

    expenditureSubCategory,
    setExpenditureSubCategory,
    expenditureContactId,
    setExpenditureContactId,
    expenditureCategory,
    setExpenditureCategory,
    expenditurePurpose,
    setExpenditurePurpose,
    expenditureHint,
    expenditureFieldsFromHint,
    expenditureSettlesDebtId,
    setExpenditureSettlesDebtId,
    expenditureRepaysLoanId,
    setExpenditureRepaysLoanId,
    expenditureRefundOfContributionId,
    setExpenditureRefundOfContributionId,
    expenditureShowQuickCreate,
    openExpenditureQuickCreate,
    cancelExpenditureQuickCreate,

    overriddenRuleCodes,
    addOverride,
    clearOverrides,
    submission,
    setSubmission,
    canClassify,
    reset,
  };
}
